<template>
  <div class="notification-container clickable" :class="{ 'no-lobby': !hasLobby, popover }">
    <div class="lobby-trigger" @click="redirectToLobby()" v-if="hasLobby">
      <img src="@/assets/images/lobby.webp" />
    </div>
    <div ref="scheduleTrigger" class="schedule-trigger" @click="openSchedulePopup">
      <div class="icon-schedule" :class="{ active: activePopover === 'schedule' }">
        <img src="@/assets/svg/calendar.svg" />
      </div>
    </div>
    <div
      class="marquee"
      ref="container"
      @click="openNotificationPopup"
      @mouseover="marqueePause = true"
      @mouseleave="marqueePause = false"
    >
      <div
        ref="notificationTrigger"
        class="notification-trigger"
        :class="{ active: activePopover === 'notification' }"
      >
        <img src="@/assets/svg/notification.svg" />
      </div>
      <MarqueeText :paused="marqueePause" :duration="marqueeDuration">
        <span class="marquee-spacer" :style="{ 'margin-left': spacerWidth + 'px' }"></span>
        <span
          class="marquee-content"
          v-for="(nd, index) in announcements.slice(0, 5)"
          :key="`${index}${nd.publishDateTime}`"
          >{{ nd.message }}</span
        >
      </MarqueeText>
    </div>
  </div>
</template>

<script>
import MarqueeText from 'vue-marquee-text-component';
import { isMobile } from 'mobile-device-detect';
import { mapActions, mapGetters } from 'vuex';
import { PopupPage } from '@/utils/index.model';
import { isEmpty } from 'lodash';
// import { mathRound } from '@/utils/helpers';

export default {
  components: {
    MarqueeText,
  },
  props: {
    /*
     * Desktop v2 shows the notification and schedule lists as popovers it owns:
     * the icons emit `toggle-popover` (with the icon's element, to anchor the
     * popover's arrow) instead of opening the menu modal.
     */
    popover: {
      type: Boolean,
      default: false,
    },
    // 'notification' | 'schedule' | '' -- frames that icon while its popover is open.
    activePopover: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      mobile: false,
      marqueeDuration: 28,
      marqueePause: false,
      spacerWidth: 1114,
      isNotificationPopupOpen: false,
      isMobile,
      isEmpty,
    };
  },
  watch: {
    announcements: {
      handler(newVal) {
        if (!newVal || newVal.length === 0) return;

        this.calculateMarqueeSpeed(newVal);
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('announcement', ['announcements']),
    ...mapGetters('general', ['lobbyUrl']),
    hasLobby() {
      return this.lobbyUrl && this.lobbyUrl.length > 0;
    },
  },
  methods: {
    ...mapActions('menu', ['updateMenu']),
    redirectToLobby() {
      let url = this.lobbyUrl;
      // Ensure URL has protocol, otherwise it's treated as relative path
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.location.href = url;
    },
    openNotificationPopup() {
      if (this.popover) {
        this.$emit('toggle-popover', { type: 'notification', el: this.$refs.notificationTrigger });
        return;
      }
      this.updateMenu({ isPopupOpen: true, activePopupPage: PopupPage.notification });
    },
    openSchedulePopup() {
      if (this.popover) {
        this.$emit('toggle-popover', { type: 'schedule', el: this.$refs.scheduleTrigger });
        return;
      }
      this.updateMenu({ isPopupOpen: true, activePopupPage: PopupPage.schedule });
    },
    calculateMarqueeSpeed(notiList) {
      const speed = 70; // Pixels per second (adjust to control speed)
      let totalWidth = 0;

      // Create a temporary element to measure text width
      const tempElement = document.createElement('span');
      tempElement.style.fontSize = '12px'; // Match .marquee-content font size
      tempElement.style.fontFamily = getComputedStyle(document.body).fontFamily; // Match font family
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.whiteSpace = 'nowrap'; // Ensure text is measured as a single line
      document.body.appendChild(tempElement);

      // Calculate total width of all messages
      notiList.slice(0, 5).forEach((n) => {
        tempElement.textContent = n.message;
        totalWidth += tempElement.offsetWidth + 40; // Include margin-right from .marquee-content
      });

      document.body.removeChild(tempElement);

      // Add spacer width to account for the gap between cycles
      totalWidth += this.spacerWidth;

      // Calculate duration: duration = totalWidth / speed
      const duration = totalWidth / speed;

      this.marqueeDuration = duration;
    },
    getSpacerWidth() {
      if (this.$refs.container) {
        this.spacerWidth = this.$refs.container.clientWidth;
      }
    },
  },
  mounted() {
    this.getSpacerWidth();
  },
};
</script>

<style lang="scss" scoped>
.notification-container {
  position: relative;
  font-size: 12px;
  overflow: hidden;
  margin-right: 8px;
  height: 30px;
  width: 100%;
  color: #c0c7d3;
  z-index: 1001;

  .lobby-trigger,
  .schedule-trigger,
  .notification-trigger {
    position: absolute;
    top: 4px;
    overflow: hidden;
    height: 24px;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    cursor: pointer;
  }
  .lobby-trigger {
    left: 4px;
    border-right: solid 1px #323c46;
    padding-right: 5px;
    margin-right: 5px;
  }
  .schedule-trigger {
    left: 37px;
    .icon-schedule {
      border-right: solid 1px #323c46;
      padding-right: 5px;
      margin-right: 5px;
      cursor: pointer;
    }
  }
  .notification-trigger {
    left: 70px;
  }
  img {
    height: 24px;
    width: 24px;
  }

  .marquee {
    padding-left: 98px;
    box-sizing: border-box;
    line-height: 30px;
    @media screen and (max-width: $tablet-portrait-width) {
      line-height: 30px;
    }
  }

  // When lobby icon is not displayed, shift icons to the left
  &.no-lobby {
    .schedule-trigger {
      left: 4px;
    }
    .notification-trigger {
      left: 37px;
    }
    .marquee {
      padding-left: 65px;
    }
  }

  /*
   * Popover mode (desktop v2): calendar and bell each sit in a 36x28 box (2px rim
   * + 4px padding around the 24px icon), framed gold on a dark fill while its
   * popover is open, with the divider moved from between them to after the bell.
   * Offsets below are for the lobby case; `no-lobby` shifts everything 38px left.
   */
  &.popover {
    .schedule-trigger,
    .notification-trigger {
      top: 1px;
      height: 28px;
    }

    .schedule-trigger {
      left: 38px;
    }

    .notification-trigger {
      left: 78px;
    }

    .icon-schedule,
    .notification-trigger {
      height: 28px;
      margin: 0;
      padding: 0 4px;
      border: 2px solid transparent;
      border-radius: 6px;
      box-sizing: border-box;
      display: flex;
      align-items: center;

      &.active {
        border-color: #8f7a52;
        background-color: #22231f;
      }
    }

    &::after {
      content: '';
      position: absolute;
      top: 6px;
      left: 122px;
      width: 1px;
      height: 18px;
      background-color: #323c46;
    }

    .marquee {
      padding-left: 131px;
    }

    &.no-lobby {
      .schedule-trigger {
        left: 0;
      }

      .notification-trigger {
        left: 40px;
      }

      &::after {
        left: 84px;
      }

      .marquee {
        padding-left: 93px;
      }
    }
  }

  // Each span gap
  .marquee-content {
    margin-right: 40px;
    font-size: 12px;
    -webkit-text-size-adjust: 100%;
  }
}
</style>
